import React, { useEffect, useRef, useState } from 'react';
import TypingIndicator from './TypingIndicator';
import TypewriterTrail from './TypewriterTrail';
import TerminalInput from './TerminalInput';

const ChatMessages = ({ messages, isTyping, onSendMessage, inputDisabled, inputPlaceholder, userTag = 'guest' }) => {
  const messagesEndRef = useRef(null);
  const [typewriterMessages, setTypewriterMessages] = useState(new Set());
  const [completedMessages, setCompletedMessages] = useState(new Set());
  
  // Check if any typewriter is currently active
  const isTypewriterActive = typewriterMessages.size > 0;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToBottomImmediate = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Enhanced scroll behavior for mobile keyboard
  useEffect(() => {
    const handleKeyboardVisibility = () => {
      // Check if keyboard is visible by looking for the CSS class
      if (document.body.classList.contains('keyboard-visible')) {
        // Use immediate scroll when keyboard is visible for better UX
        setTimeout(scrollToBottomImmediate, 100);
      } else {
        scrollToBottom();
      }
    };

    // Listen for keyboard visibility changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          handleKeyboardVisibility();
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Track which messages should use typewriter effect
  useEffect(() => {
    const latestBotMessage = messages
      .slice()
      .reverse()
      .find(msg => msg.sender === 'bot');
    
    if (latestBotMessage) {
      const messageId = messages.indexOf(latestBotMessage);
      if (!completedMessages.has(messageId)) {
        setTypewriterMessages(new Set([messageId]));
      }
    }
  }, [messages, completedMessages]);

  const handleTypewriterComplete = (messageIndex) => {
    setCompletedMessages(prev => new Set([...prev, messageIndex]));
    setTypewriterMessages(prev => {
      const newSet = new Set(prev);
      newSet.delete(messageIndex);
      return newSet;
    });
  };

  return (
    <div className="chat-messages" id="chatMessages">
      {messages.map((message, index) => (
        <div 
          key={index} 
          className={`message-wrapper ${message.sender}-message-wrapper`}
        >
          <div className={`message-bubble ${message.sender}-message-bubble`}>
            <div className={`message ${message.sender}-message`}>
              {message.sender === 'bot' && typewriterMessages.has(index) ? (
                <div className="bot-message-line">
                  {index > 0 && <span className="bot-terminal-prompt" aria-hidden="true">ankur ~ % </span>}
                  <TypewriterTrail
                    text={message.text}
                    speed={10}
                    onComplete={() => handleTypewriterComplete(index)}
                    className="bot-typewriter"
                  />
                </div>
              ) : message.sender === 'bot' ? (
                <div className="bot-message-line">
                  {index > 0 && <span className="bot-terminal-prompt" aria-hidden="true">ankur ~ % </span>}
                  <span className="bot-message-text">{message.text}</span>
                </div>
              ) : message.sender === 'user' ? (
                <div className="user-message-line">
                  <span className="terminal-prompt" aria-hidden="true">{userTag} ~ % </span>
                  <span className="user-message-text">{message.text}</span>
                </div>
              ) : (
                message.text
              )}
            </div>
          </div>
        </div>
      ))}
      <TypingIndicator isTyping={isTyping} />
      <TerminalInput 
        onSendMessage={onSendMessage}
        inputDisabled={inputDisabled}
        inputPlaceholder={inputPlaceholder}
        isTyping={isTyping || isTypewriterActive}
        userTag={userTag}
      />
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
